import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { addApplication, updateApplication } from "../redux/applicationsSlice";
import useForm from "../hooks/useForm";
import { User, Briefcase, FileText, CheckCircle2, ChevronRight, ChevronLeft, Plus, X } from "lucide-react";

const ApplicationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Find application by ID or JobID (for safer editing context)
  const existingApplication = useSelector((state) =>
    state.applications.find((app) => app.id === parseInt(id))
  );

  const [step, setStep] = useState(1);
  const [successMsg, setSuccessMsg] = useState("");
  const [skillsInput, setSkillsInput] = useState("");
  const [skillsList, setSkillsList] = useState(
    existingApplication && existingApplication.skills
      ? existingApplication.skills.split(",").map((s) => s.trim())
      : []
  );

  const validate = (values) => {
    const errors = {};
    if (!values.name) errors.name = "Name is required";
    if (!values.email) errors.email = "Email is required";
    if (!values.phone) errors.phone = "Phone number is required";
    return errors;
  };

  const initialFormValues = useMemo(
    () =>
      existingApplication
        ? {
            name: existingApplication.name || "",
            email: existingApplication.email || "",
            phone: existingApplication.phone || "",
            experience: existingApplication.experience || "",
            skills: existingApplication.skills || "",
            coverLetter: existingApplication.coverLetter || "",
            startDate: existingApplication.startDate || "",
          }
        : {
            name: "",
            email: "",
            phone: "",
            experience: "",
            skills: "",
            coverLetter: "",
            startDate: "",
          },
    [existingApplication]
  );

  const { values, errors, handleChange, handleSubmit, setValues } = useForm(
    initialFormValues,
    validate
  );

  useEffect(() => {
    if (existingApplication) {
      setValues(initialFormValues);
      setSkillsList(
        initialFormValues.skills
          ? initialFormValues.skills.split(",").map((s) => s.trim())
          : []
      );
    }
  }, [existingApplication, initialFormValues, setValues]);

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleAddSkill = () => {
    if (skillsInput.trim() && !skillsList.includes(skillsInput.trim())) {
      setSkillsList([...skillsList, skillsInput.trim()]);
      setSkillsInput("");
    }
  };

  const handleRemoveSkill = (skill) => {
    setSkillsList(skillsList.filter((s) => s !== skill));
  };

  const handleFinalSubmit = () => {
    const finalValues = { ...values, skills: skillsList.join(", ") };
    if (existingApplication) {
      dispatch(
        updateApplication({
          id: existingApplication.id,
          jobId: existingApplication.jobId,
          ...finalValues,
        })
      );
      setSuccessMsg("Application updated successfully!");
    } else {
      const applicationId = Date.now();
      dispatch(
        addApplication({ id: applicationId, jobId: id, ...finalValues })
      );
      setSuccessMsg("Application submitted successfully!");
    }
    
    setTimeout(() => {
      navigate("/applications");
    }, 2000);
  };

  const steps = [
    { n: 1, label: "Personal", icon: <User size={18} /> },
    { n: 2, label: "Experience", icon: <Briefcase size={18} /> },
    { n: 3, label: "Additional", icon: <FileText size={18} /> },
  ];

  if (successMsg) {
    return (
      <div className="max-w-xl mx-auto mt-20 p-12 glass-card rounded-3xl text-center border border-white/40">
        <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
          <CheckCircle2 size={48} />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Success!</h2>
        <p className="text-slate-500 mb-8">{successMsg}</p>
        <p className="text-sm text-slate-400">Redirecting to applications...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      {/* Progress Stepper */}
      <div className="flex justify-between mb-10 relative px-2">
        <div className="absolute top-5 left-0 w-full h-0.5 bg-slate-200 -z-10 -translate-y-1/2"></div>
        <div 
          className="absolute top-5 left-0 h-0.5 bg-blue-600 -z-10 -translate-y-1/2 transition-all duration-300"
          style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
        ></div>
        {steps.map((s) => (
          <div key={s.n} className="flex flex-col items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              step >= s.n ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30" : "bg-white text-slate-400 border-2 border-slate-200"
            }`}>
              {step > s.n ? <CheckCircle2 size={20} /> : s.icon}
            </div>
            <span className={`text-[10px] sm:text-xs font-bold ${step >= s.n ? "text-blue-600" : "text-slate-400"}`}>
              {s.label}
            </span>
          </div>
        ))}
      </div>

      <div className="glass-card rounded-3xl p-5 sm:p-8 border border-white/40 shadow-xl shadow-slate-900/5">
        <h1 className="text-2xl font-bold text-slate-900 mb-8">
          {existingApplication ? "Edit Application" : "Job Application"}
        </h1>

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Full Name</label>
              <input
                className="input-field"
                name="name"
                placeholder="Hemant Patil"
                value={values.name}
                onChange={handleChange}
              />
              {errors.name && <p className="text-red-500 text-xs mt-2 px-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Email Address</label>
              <input
                className="input-field"
                name="email"
                type="email"
                placeholder="hemant@example.com"
                value={values.email}
                onChange={handleChange}
              />
              {errors.email && <p className="text-red-500 text-xs mt-2 px-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Phone Number</label>
              <input
                className="input-field"
                name="phone"
                placeholder="+91 1234567890"
                value={values.phone}
                onChange={handleChange}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-2 px-1">{errors.phone}</p>}
            </div>

            <button
              onClick={() => handleSubmit(nextStep)}
              className="w-full btn-primary flex items-center justify-center gap-2 mt-8"
            >
              Next Step
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Years of Experience</label>
              <select
                className="input-field appearance-none"
                name="experience"
                value={values.experience}
                onChange={handleChange}
              >
                <option value="">Select experience</option>
                <option value="0-1">0-1 years</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5+">5+ years</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Skills</label>
              <div className="flex gap-2 mb-4">
                <input
                  className="input-field"
                  placeholder="React, Tailwind, Node.js..."
                  value={skillsInput}
                  onChange={(e) => setSkillsInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddSkill}
                  className="bg-slate-900 text-white p-3 rounded-xl hover:bg-slate-800 transition-colors"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {skillsList.map((skill, idx) => (
                  <span
                    key={idx}
                    className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 border border-blue-100"
                  >
                    {skill}
                    <button onClick={() => handleRemoveSkill(skill)} className="hover:text-red-500 transition-colors">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-4 mt-8">
              <button onClick={prevStep} className="w-full sm:flex-1 btn-secondary flex items-center justify-center gap-2">
                <ChevronLeft size={18} />
                Back
              </button>
              <button
                onClick={() => {
                  setValues({ ...values, skills: skillsList.join(", ") });
                  handleSubmit(nextStep);
                }}
                className="w-full sm:flex-[2] btn-primary flex items-center justify-center gap-2"
              >
                Next Step
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Cover Letter</label>
              <textarea
                className="input-field min-h-[150px] resize-none"
                name="coverLetter"
                placeholder="Tell us why you're a great fit for this role..."
                value={values.coverLetter}
                onChange={handleChange}
              ></textarea>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 px-1">Earliest Start Date</label>
              <input
                className="input-field"
                name="startDate"
                type="date"
                value={values.startDate}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-4 mt-8">
              <button onClick={prevStep} className="w-full sm:flex-1 btn-secondary flex items-center justify-center gap-2">
                <ChevronLeft size={18} />
                Back
              </button>
              <button
                onClick={handleFinalSubmit}
                className="w-full sm:flex-[2] btn-primary flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                {existingApplication ? "Update Application" : "Submit Application"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationForm;

